import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { IconAlertTriangle } from '@tabler/icons-react'
import { Button, Flex, Group, Modal, Text } from '@mantine/core'

export const OUTAGE_POLL_INTERVAL_MS = 30_000
export const OUTAGE_RELOAD_GRACE_MS = 60_000
const OUTAGE_HEADER = 'x-app-mode'
const OUTAGE_HEADER_VALUE = 'outage'

const checkForOutage = async ({ signal }: { signal: AbortSignal }) => {
  const response = await fetch('/', { method: 'HEAD', cache: 'no-store', signal })
  return response.headers.get(OUTAGE_HEADER) === OUTAGE_HEADER_VALUE
}

// Swapping the deployed Static Web App content for the outage page (see the infra repo's
// outage-page/) only affects new page loads - a tab that already has the app open keeps running
// the in-memory build indefinitely and would otherwise never see it. This polls for the
// X-App-Mode header the outage page's staticwebapp.config.json sets and warns the user before
// reloading into it. Uses a HEAD request so each poll only reads headers, not the full page.
export const OutagePoll = () => {
  const [modalOpened, setModalOpened] = useState(false)

  const { data: outageDetected } = useQuery({
    queryKey: ['outage-poll'],
    queryFn: checkForOutage,
    // Not a GraphQL query, so the shared retryChecker doesn't apply here - a failed poll should
    // just wait for the next scheduled one rather than react-query retrying it independently.
    retry: false,
    refetchInterval: (query) => (query.state.data ? false : OUTAGE_POLL_INTERVAL_MS),
    // The whole point is to catch tabs left open and idle in the background - react-query
    // pauses refetchInterval for unfocused tabs by default, which would defeat that.
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    if (outageDetected) setModalOpened(true)
  }, [outageDetected])

  // Reload happens on its own timer regardless of whether the modal is dismissed - closing it
  // only hides the warning, it doesn't cancel or postpone the maintenance.
  useEffect(() => {
    if (!outageDetected) return

    const reloadTimeout = setTimeout(() => window.location.reload(), OUTAGE_RELOAD_GRACE_MS)
    return () => clearTimeout(reloadTimeout)
  }, [outageDetected])

  return (
    <Modal
      opened={modalOpened}
      onClose={() => setModalOpened(false)}
      centered
      title={
        <Flex justify='flex-start' align='center'>
          <IconAlertTriangle size={28} color='var(--mantine-color-yellow-4)' />
          <Text fw={500} size='lg' ml='sm'>
            Site maintenance
          </Text>
        </Flex>
      }
      closeButtonProps={{ 'aria-label': 'Close modal' }}
      returnFocus={false}
    >
      <Text>
        This site is going into maintenance in one minute. Please save any in-progress work.
      </Text>
      <Group justify='flex-end' mt='md' gap='xs'>
        <Button variant='transparent' onClick={() => setModalOpened(false)}>
          Close
        </Button>
      </Group>
    </Modal>
  )
}
